import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import {getAuth} from "firebase/auth";
import {permissions} from "../../config/permissions";
import {transformServerMessage} from "./utils";
import {Chat} from "../../pages/dashboard/chats/chat";

// Pagination types
interface Pagination {
  page: number;
  limit: number;
  totalMessages?: number;
  total?: number;
  totalPages: number;
  hasMore: boolean;
}

interface ChatQueryParams {
  chatID: string;
  page?: number;
  limit?: number;
}

interface ChatResponse {
  chat: Chat;
  pagination: Pagination;
}

interface TransferQueryParams {
  page?: number;
  limit?: number;
  status?: "SUCCEEDED" | "FAILED" | "NOT SENT";
  startDate?: string;
  endDate?: string;
}

interface TransferResponse {
  transfers: Transfer[];
  pagination: Pagination;
}

// Phase 5: Reviews pagination types
interface ReviewQueryParams {
  page?: number;
  limit?: number;
}

interface ReviewResponse {
  reviews: Review[];
  pagination: Pagination;
}

// Phase 5: Applications pagination types
interface ApplicationsQueryParams {
  page?: number;
  limit?: number;
  filter?: "new" | "rejected";
}

interface ApplicationsResponse {
  users: User[];
  pagination: Pagination;
}

// Phase 5: Users pagination types
interface UsersQueryParams {
  page?: number;
  email?: string | null;
  name?: string | null;
  searchQuery?: string | null;
}

interface UsersResponse {
  users: User[];
  pagination: Pagination;
}

export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/admin",
    prepareHeaders: async (headers, {getState}) => {
      const auth = getAuth();

      const token = auth.currentUser
        ? await auth.currentUser.getIdToken(true)
        : null;

      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }

      return headers;
    },
    fetchFn: async (input) => {
      const controller = new AbortController();
      const id = setTimeout(() => {
        controller.abort();
      }, 5000);
      const res = await fetch(input);
      clearTimeout(id);
      return Promise.resolve(res);
    },
  }),
  tagTypes: [
    "User",
    "Chat",
    "Transfer",
    "Review",
    "Report",
    "ChatValueCode",
    "Category",
    "BlockUser",
    "ChatRequestAnalytics",
  ],
  endpoints: (builder) => ({
    getUser: builder.query<User, string>({
      query: (userID) => ({url: `user/${userID}`}),
      providesTags: ["User"],
    }),

    // Phase 5: Updated to use pagination response
    getUsers: builder.query<UsersResponse, UsersQueryParams>({
      query: ({ page = 1, email, name, searchQuery } = {}) => ({
        url: `user?page=${page}` +
          `${email != null ? `&email=${email}` : ""}` +
          `${name != null ? `&name=${name}` : ""}` +
          `${searchQuery != null ? `&searchQuery=${searchQuery}` : ""}`
      }),
      providesTags: ["User"],
    }),
    getPsychics: builder.query<User[], number | void>({
      query: (page: number) => ({url: `psychic?page=${page ?? 1}`}),
      providesTags: ["User"],
    }),
    // Phase 5: Updated to use pagination with server-side filtering
    getApplications: builder.query<ApplicationsResponse, ApplicationsQueryParams>({
      query: ({ page = 1, limit = 50, filter } = {}) => ({
        url: `applications?page=${page}&limit=${limit}${filter ? `&filter=${filter}` : ''}`
      }),
      providesTags: ["User"],
    }),
    updateUser: builder.mutation<User, Partial<User> & Pick<User, "userID">>({
      query: ({userID, ...patch}) => ({
        url: `user/${userID}`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: ["User"],
    }),
    uploadAvatar: builder.mutation<User, { userID: string; image: string }>({
      query: ({userID, image}) => ({
        url: `user/${userID}/profile-picture`,
        method: "POST",
        body: { image },
      }),
      invalidatesTags: ["User"],
    }),
    createUser: builder.mutation<
      { user: User; tempPassword: string; message: string },
      {
        email: string;
        name: string;
        user_role: "user" | "psychic";
        enabled?: boolean;
        approved?: boolean;
        stripe_active?: boolean;
        subscription_only?: boolean;
        available?: boolean;
        bio?: string;
        chat_rate?: number;
      }
    >({
      query: (userData) => ({
        url: "user",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["User"],
    }),
    getCategories: builder.query<any[], null>({
      query: () => ({url: "categories"}),
      providesTags: ["Category"],
    }),
    getChats: builder.query<Chat[], { page: number, email: string }>({
      query: ({page, email}) => ({url: `chat?page=${page ?? 1}&email=${email ?? ""}`}),
      providesTags: ["Chat"],
    }),
    getChatRequestAnalytics: builder.query<{ analytics: ChatRequestAnalytics[], userStats: any[] }, number | void>({
      // getChatRequestAnalytics: builder.query<ChatRequestAnalytics[], number | void>({
      query: () => ({url: `chat-request-analytics`}),
      providesTags: ["ChatRequestAnalytics"],
    }),
    getChat: builder.query<ChatResponse, ChatQueryParams>({
      query: ({ chatID, page = 1, limit = 100 }) => ({
        url: `chat/${chatID}?page=${page}&limit=${limit}`
      }),
      providesTags: (result, error, arg) => [{type: "Chat", id: arg.chatID}],
      transformResponse(response: { chat: Chat; pagination: Pagination }) {
        const { chat, pagination } = response;
        chat.messages = transformServerMessage(
          chat.messages,
          chat.users
        ) as ChatMessage[];
        return { chat, pagination };
      },
    }),
    getTransfers: builder.query<TransferResponse, TransferQueryParams>({
      query: ({ page = 1, limit = 100, status, startDate, endDate }) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(limit));
        if (status) params.set("status", status);
        if (startDate) params.set("startDate", startDate);
        if (endDate) params.set("endDate", endDate);
        return { url: `transfer?${params.toString()}` };
      },
      providesTags: ["Transfer"],
    }),
    getGroupedPayouts: builder.query<any[], null>({
      query: () => ({url: "psychic-payouts"}),
    }),
    updateTransfers: builder.mutation<Transfer, Transfer[]>({
      query: (transfers) => ({
        url: `transfer`,
        method: "PUT",
        body: transfers,
      }),
      invalidatesTags: ["Transfer"],
    }),
    // Phase 5: Updated to use pagination
    getReviews: builder.query<ReviewResponse, ReviewQueryParams>({
      query: ({ page = 1, limit = 50 } = {}) => ({
        url: `review?page=${page}&limit=${limit}`
      }),
      providesTags: ["Review"],
    }),
    updateReview: builder.mutation<
      Review,
      Partial<Review> & Pick<Review, "reviewID">
    >({
      query: ({reviewID, ...patch}) => ({
        url: `review/${reviewID}`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: ["Review"],
    }),
    removeReview: builder.mutation<
      Review,
      Partial<Review> & Pick<Review, "reviewID">
    >({
      query: ({reviewID, ...patch}) => ({
        url: `review/${reviewID}`,
        method: "DELETE",
        body: patch,
      }),
      invalidatesTags: ["Review"],
    }),
    getReports: builder.query<UserReport[], null>({
      query: () => ({url: "report"}),
      providesTags: ["Report"],
    }),
    updateReport: builder.mutation<
      UserReport,
      Partial<UserReport> & Pick<UserReport, "id">
    >({
      query: ({id, ...patch}) => ({
        url: `report/${id}`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: ["Report"],
    }),
    getChatValueCodes: builder.query<ChatValueCode[], null>({
      query: () => ({url: "chat-value-code"}),
      providesTags: ["ChatValueCode"],
    }),
    getBlockedUsers: builder.query<BlockUser[], null>({
      query: () => ({
        url: "user/blocked",
      }),
    }),
    postBlockUser: builder.mutation<{ email: string }, { email: string }>({
      query: (data) => ({
        url: "user/block-email",
        method: "POST",
        body: data,
      }),
    }),
    patchBlockUser: builder.mutation<null, { id: string }>({
      query: ({id}) => ({
        url: "user/block-email/" + id,
        method: "PATCH",
      }),
    }),

    getSubscribedUsers: builder.query<User[], number | void>({
      query: (page: number) => ({url: `subscribed?page=${page ?? 1}`}),
      providesTags: ["User"],
    }),
    getSubscriptionPlans: builder.query<any[], void>({
      query: () => ({url: `subscription-plans`}),
    }),
    getUserSubscriptionSessions: builder.query<any, string>({
      query: (userID) => ({url: `user/${userID}/subscription-sessions`}),
      providesTags: ["User"],
    }),
    waiveChatSession: builder.mutation<any, string>({
      query: (sessionId) => ({
        url: `chat-session/${sessionId}/waive`,
        method: "POST",
      }),
      invalidatesTags: ["User"],
    }),
    unwaiveChatSession: builder.mutation<any, string>({
      query: (sessionId) => ({
        url: `chat-session/${sessionId}/unwaive`,
        method: "POST",
      }),
      invalidatesTags: ["User"],
    }),
    resetSubscriptionSessions: builder.mutation<any, { userId: string; reason?: string }>({
      query: ({ userId, reason }) => ({
        url: `user/${userId}/subscription-sessions/reset`,
        method: "POST",
        body: { confirm: true, reason },
      }),
      invalidatesTags: ["User"],
    }),
    // Subscription Plan Limits endpoints
    getSubscriptionPlanLimits: builder.query<any, void>({
      query: () => 'subscription-plan-limits',
      providesTags: ['User'],
    }),
    createOrUpdatePlanLimit: builder.mutation<any, {
      price_id: string;
      plan_name: string;
      session_limit: number;
      session_minutes: number;
    }>({
      query: (data) => ({
        url: 'subscription-plan-limits',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
    deletePlanLimit: builder.mutation<any, string>({
      query: (priceId) => ({
        url: `subscription-plan-limits/${priceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useUpdateUserMutation,
  useUploadAvatarMutation,
  useCreateUserMutation,
  useGetChatQuery,
  useGetChatsQuery,
  useGetUserQuery,
  useGetCategoriesQuery,
  useGetTransfersQuery,
  useGetReviewsQuery,
  useUpdateReviewMutation,
  useGetReportsQuery,
  useUpdateReportMutation,
  useGetChatValueCodesQuery,
  useUpdateTransfersMutation,
  usePostBlockUserMutation,
  useGetBlockedUsersQuery,
  usePatchBlockUserMutation,
  useGetChatRequestAnalyticsQuery,
  useGetPsychicsQuery,
  useGetApplicationsQuery,
  useRemoveReviewMutation,
  useGetGroupedPayoutsQuery,
  useGetSubscribedUsersQuery,
  useGetSubscriptionPlansQuery,
  useGetUserSubscriptionSessionsQuery,
  useWaiveChatSessionMutation,
  useUnwaiveChatSessionMutation,
  useResetSubscriptionSessionsMutation,
  useGetSubscriptionPlanLimitsQuery,
  useCreateOrUpdatePlanLimitMutation,
  useDeletePlanLimitMutation,
} = adminApi;
